import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Empty, Form, Image, Input, Modal, Popconfirm, Row, Select, Space, Tabs, Tag, Typography, Upload, message } from 'antd';
import { InboxOutlined, ReloadOutlined, UserOutlined } from '@ant-design/icons';
import { filmApi } from '../../services/api/film';
import type { FaceCandidate, FilmCharacter, MyAssetItem, SeedanceDigitalCharacter } from '../../types/film';
import { apiErrorText } from './apiErrorText';
import PromptChipEditor from './PromptChipEditor'; // 261010 P9D #29 — ô prompt tạo mặt = chip
import { faceTextOf } from './promptSurfaceUtils';
import { faceUrlOf } from './characterCardUtils';
import { AGE_BUCKETS, ageRange, defaultFacePrompt, faceCandidateModelLabel, faceNotes, faceSourceChip, isCandidateInUse, shouldSendPromptOverride, showUploadedFaceBlock } from './faceSourceUtils';

const { Text, Paragraph } = Typography;

/** Quốc gia nhiều avatar nhất trong catalog (đo dev 24/09) — ô lọc gõ được tên khác. */
const COUNTRIES = ['China', 'United States', 'Brazil', 'Japan', 'Indonesia', 'Ethiopia', 'Mexico', 'United Kingdom', 'France', 'South Korea',
  'Uzbekistan', 'Russia', 'Germany', 'Thailand', 'Spain', 'Peru', 'South Africa', 'Malaysia', 'Argentina', 'Italy', 'Vietnam', 'Kazakhstan',
  'Colombia', 'Netherlands', 'Nigeria', 'Turkey', 'Venezuela', 'Philippines', 'Australia', 'Jamaica', 'India', 'Canada', 'Poland', 'Iran'];

type TabKey = 'library' | 'mine' | 'upload' | 'text';

export interface FaceSourceModalProps {
  character: FilmCharacter | null;
  onClose: () => void;
  /** Đã đổi nguồn mặt / avatar ⇒ nạp lại Bank (looks + refs). */
  onSaved: () => void;
  /** 260930 P2 — tập đang mở: bối cảnh/thể loại của tập cho bộ tạo mặt và dựng ảnh bộ chính. */
  planId?: string | null;
}

/**
 * Hộp *Mặt nhân vật* (đợt 260922 E3-P5) — thay modal "Digital Character (Seedance)".
 *
 * Ba nguồn mặt, người chọn cái nào thì ảnh bộ chính dựng từ cái đó (`film_characters.face_source`):
 *   · **Thư viện avatar** — lưới catalog có lọc + gợi ý theo mô tả (server xếp, `rankFor`). Chọn avatar = GÁN DC như trước: clip BytePlus
 *     vẫn cần neo mặt tới Epic 5. Giữ đủ chức năng cũ: tìm chữ, tab My Asset, dán asset id.
 *   · **Tải ảnh mặt lên** — chỉ cho ảnh bộ đồ.
 *   · **Tạo mặt (AI)** — (260930 P2) MỘT mặt mỗi lần bấm (Flare, lùi NB2 khi bị từ chối), muốn khác thì sửa ô Prompt → *Dùng mặt này* ⇒ nguồn `AI_FACE` + tự dựng ảnh bộ chính. Giữ link *Dùng mô tả, không ảnh mặt*.
 */
const FaceSourceModal: React.FC<FaceSourceModalProps> = ({ character: c, onClose, onSaved, planId }) => {
  const [tab, setTab] = useState<TabKey>('library');
  const [busy, setBusy] = useState<string | null>(null);
  // ── thư viện ──
  const [catalog, setCatalog] = useState<SeedanceDigitalCharacter[]>([]);
  const [loading, setLoading] = useState(false);
  const [gender, setGender] = useState<string>('auto');
  const [age, setAge] = useState<string>('all');
  const [country, setCountry] = useState<string | undefined>();
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<SeedanceDigitalCharacter | null>(null);
  const [form] = Form.useForm();
  const pickedId: string | undefined = Form.useWatch('seedanceCharacterId', form);
  const pickedSourceUrl: string | undefined = Form.useWatch('previewSourceUrl', form);
  // ── My Asset (asset tự upload lên ModelArk — link ảnh hết hạn ~11.5 giờ, gọi thẳng API) ──
  const [myAssets, setMyAssets] = useState<MyAssetItem[]>([]);
  const [myLoading, setMyLoading] = useState(false);
  // ── tải ảnh mặt ──
  const [file, setFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  // ── tạo mặt AI (260930 P2) ──
  const [cands, setCands] = useState<FaceCandidate[]>([]);
  const [candLoading, setCandLoading] = useState(false);
  const [candWarn, setCandWarn] = useState<string[]>([]);
  const [candErr, setCandErr] = useState<string | null>(null);
  const [choosingId, setChoosingId] = useState<string | null>(null);
  // Ô Prompt: điền sẵn prompt của ứng viên mới nhất (trừ khi người đã sửa); rỗng = prompt mặc định của server.
  const [promptText, setPromptText] = useState('');
  const [promptRev, setPromptRev] = useState(0); // chip editor nạp chữ một lần ⇒ đổi khi prompt bị thay từ code
  const chipChoices = useMemo(() => (c ? [{ kind: 'char' as const, id: c.id, label: c.name, previewUrl: faceUrlOf(c as any), ready: true }] : []), [c]); // eslint-disable-line react-hooks/exhaustive-deps
  const [promptEdited, setPromptEdited] = useState(false);

  const loadCatalog = useCallback(async (q?: { search?: string; gender?: string; age?: string; country?: string }) => {
    if (!c) return;
    setLoading(true);
    try {
      const g = q?.gender ?? gender;
      setCatalog(await filmApi.listSeedanceCatalog({
        rankFor: c.id, limit: 120,
        ...(g !== 'auto' ? { gender: g } : {}),
        ...ageRange(q?.age ?? age),
        ...((q?.country ?? country) ? { country: q?.country ?? country } : {}),
        ...((q?.search ?? search).trim() ? { search: (q?.search ?? search).trim() } : {}),
      }));
    } catch (e: any) { message.error(apiErrorText(e, 'Đọc thư viện avatar')); }
    finally { setLoading(false); }
  }, [c, gender, age, country, search]);

  const loadMyAssets = useCallback(async (force?: boolean) => {
    setMyLoading(true);
    try { setMyAssets(await filmApi.listMyAssets(force)); }
    catch (e: any) { message.error(apiErrorText(e, 'Đọc My Asset')); }
    finally { setMyLoading(false); }
  }, []);

  // Mở hộp cho nhân vật khác ⇒ về trạng thái đầu, tab theo nguồn đang dùng.
  useEffect(() => {
    if (!c) return;
    setTab(c.faceSource === 'UPLOAD' ? 'upload' : c.faceSource === 'TEXT' || c.faceSource === 'AI_FACE' ? 'text' : 'library');
    setCands([]); setCandWarn([]); setCandErr(null); setChoosingId(null); setPromptText(''); setPromptEdited(false);
    filmApi.listFaceCandidates(c.id).then((l) => { setCands(l); setPromptText(defaultFacePrompt(l)); setPromptRev((n) => n + 1); }).catch(() => setCands([]));
    setGender('auto'); setAge('all'); setCountry(undefined); setSearch(''); setPicked(null);
    setFile(null); setFilePreview(null);
    form.setFieldsValue({ seedanceCharacterId: c.seedanceCharacterId || '', previewSourceUrl: '' });
    loadCatalog({ search: '', gender: 'auto', age: 'all', country: undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c?.id]);

  useEffect(() => () => { if (filePreview) URL.revokeObjectURL(filePreview); }, [filePreview]);

  if (!c) return null;
  const chip = faceSourceChip(c);
  const notes = faceNotes(c);
  const done = (text: string) => { message.success(text); onSaved(); onClose(); };

  const saveDc = async () => {
    const { seedanceCharacterId, previewSourceUrl } = await form.validateFields();
    const id = String(seedanceCharacterId || '').trim();
    if (!id) { message.warning('Chọn một avatar hoặc dán asset id'); return; }
    // Chọn từ thư viện ⇒ ảnh R2 sẵn có. Dán id tay ⇒ tải từ URL nguồn; không có URL ⇒ server lấy ảnh catalog của id MỚI (không giữ ảnh cũ).
    const opts = picked?.assetId === id && picked.previewUrl
      ? { previewUrl: picked.previewUrl }
      : { previewSourceUrl: String(previewSourceUrl || '').trim() || undefined };
    setBusy('dc');
    try { await filmApi.setSeedanceCharacter(c.id, id, opts); done(`Đã gán avatar cho ${c.name} — ảnh bộ đồ dựng từ avatar này`); }
    catch (e: any) { message.error(apiErrorText(e, 'Gán avatar')); }
    finally { setBusy(null); }
  };
  const clearDc = async () => {
    setBusy('clear');
    try { await filmApi.setSeedanceCharacter(c.id, null); done(`Đã bỏ gán avatar của ${c.name}`); }
    catch (e: any) { message.error(apiErrorText(e, 'Bỏ gán avatar')); }
    finally { setBusy(null); }
  };
  const chooseSource = async (source: 'DC' | 'UPLOAD' | 'TEXT', text: string) => {
    setBusy(source);
    try { await filmApi.setFaceSource(c.id, source); done(text); }
    catch (e: any) { message.error(apiErrorText(e, 'Đổi nguồn mặt')); }
    finally { setBusy(null); }
  };
  const uploadFace = async () => {
    if (!file) return;
    setBusy('upload');
    try { await filmApi.uploadFace(c.id, file); done(`Đã tải ảnh mặt cho ${c.name} — ảnh bộ đồ dựng từ ảnh này`); }
    catch (e: any) { message.error(apiErrorText(e, 'Tải ảnh mặt')); }
    finally { setBusy(null); }
  };

  // 🔴 POST tạo mặt lỗi (kể cả 504 gateway): server có thể VẪN đang chạy / đã lưu ứng viên ⇒ tự GET lại danh sách. 409 = khoá việc đang chạy (câu lỗi từ server).
  const createCandidates = async () => {
    setBusy('cands'); setCandLoading(true); setCandErr(null); setCandWarn([]);
    const sendText = faceTextOf(promptText); // chip → nhãn chữ (gen từ chữ)
    const send = shouldSendPromptOverride(sendText);
    const refill = (l: FaceCandidate[]) => { setCands(l); if (!promptEdited) { setPromptText(defaultFacePrompt(l)); setPromptRev((n) => n + 1); } };
    try {
      const r = await filmApi.createFaceCandidates(c.id, { planId, ...(send ? { promptOverride: sendText } : {}) });
      setCandWarn(r.warnings || []);
      refill(await filmApi.listFaceCandidates(c.id));
    } catch (e: any) {
      setCandErr(apiErrorText(e, 'Tạo mặt'));
      if (e?.response?.status !== 409) {
        setCandWarn(['Có thể vẫn đang chạy ở server — đợi ít giây rồi mở lại hộp để xem mặt đã lưu.']);
      }
      filmApi.listFaceCandidates(c.id).then(refill).catch(() => undefined);
    } finally { setBusy(null); setCandLoading(false); }
  };
  const chooseCandidate = async (v: FaceCandidate) => {
    setBusy('choose'); setChoosingId(v.id);
    try {
      const r = await filmApi.chooseFaceCandidate(c.id, v.id, { planId });
      (r.warnings || []).forEach((w) => message.warning(w, 8));
      done(r.look ? `Đã dùng mặt này cho ${c.name} — ảnh bộ chính đã dựng lại` : `Đã dùng mặt này cho ${c.name}`);
    } catch (e: any) { message.error({ content: apiErrorText(e, 'Dùng mặt này'), duration: 8 }); }
    finally { setBusy(null); setChoosingId(null); }
  };

  const avatarCard = (it: SeedanceDigitalCharacter) => {
    const selected = picked?.assetId === it.assetId || pickedId === it.assetId;
    const current = it.assetId === c.seedanceCharacterId;
    return (
      <Col key={it.assetId} xs={12} sm={8} md={6}>
        <Card size="small" hoverable styles={{ body: { padding: 6 } }}
          onClick={() => { setPicked(it); form.setFieldsValue({ seedanceCharacterId: it.assetId, previewSourceUrl: '' }); }}
          style={{ border: selected ? '2px solid #1677ff' : undefined, opacity: it.usedBy ? 0.45 : 1 }}
          cover={it.previewUrl
            ? <img src={it.previewUrl} alt={it.title || ''} style={{ height: 130, objectFit: 'cover' }} />
            : <div style={{ height: 130, background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><UserOutlined /></div>}>
          <div style={{ fontSize: 11, lineHeight: 1.3 }}>
            <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.title || it.assetId}</div>
            <Text type="secondary" style={{ fontSize: 10 }}>{[it.gender === 'female' ? 'Nữ' : it.gender === 'male' ? 'Nam' : null, it.age, it.country].filter(Boolean).join(' · ')}</Text>
            {current && <div><Tag color="blue" style={{ fontSize: 10, marginTop: 2 }}>Đang gán</Tag></div>}
            {it.usedBy && <div style={{ fontSize: 10, color: '#b45309' }}>Đang dùng: {it.usedBy}</div>}
          </div>
        </Card>
      </Col>
    );
  };

  const previewUrl = picked?.previewUrl || (pickedId ? pickedSourceUrl : '') || (pickedId && pickedId === c.seedanceCharacterId ? c.seedancePreviewUrl : '');
  const dcForm = (
    <>
      <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
        <Form.Item name="seedanceCharacterId" label="Asset id (chọn ở lưới trên, hoặc dán tay)" style={{ marginBottom: 8 }}>
          <Input placeholder="vd asset-20260225022034-thhzm" allowClear />
        </Form.Item>
        <Form.Item name="previewSourceUrl" label="URL ảnh nguồn (chỉ khi dán id tay — ảnh signed, sẽ tải về R2; bỏ trống thì lấy ảnh catalog)" hidden={!!picked} style={{ marginBottom: 8 }}>
          <Input placeholder="https://...volces.com/...png?X-Tos-..." allowClear />
        </Form.Item>
      </Form>
      <Space align="end" style={{ width: '100%', justifyContent: 'space-between' }}>
        {previewUrl ? <Image src={previewUrl} height={90} style={{ objectFit: 'cover', borderRadius: 4 }} /> : <span />}
        <Space>
          {c.seedanceCharacterId && (
            <Popconfirm title="Bỏ gán avatar?" description="Clip BytePlus mất neo mặt của nhân vật này (tới Epic 5)." okText="Bỏ gán" okButtonProps={{ danger: true }} cancelText="Thôi" onConfirm={clearDc}>
              <Button danger loading={busy === 'clear'}>Bỏ gán avatar</Button>
            </Popconfirm>
          )}
          {c.seedanceCharacterId && c.faceSource !== 'DC' && (!pickedId || pickedId === c.seedanceCharacterId) && (
            <Button loading={busy === 'DC'} onClick={() => chooseSource('DC', `Ảnh bộ đồ của ${c.name} dựng từ avatar đang gán`)}>Dùng avatar đang gán</Button>
          )}
          <Button type="primary" loading={busy === 'dc'} disabled={!String(pickedId || '').trim()} onClick={saveDc}>Dùng avatar này</Button>
        </Space>
      </Space>
    </>
  );

  return (
    <Modal title={`Mặt nhân vật: ${c.name}`} open={!!c} width={820} onCancel={onClose} footer={<Button onClick={onClose}>Đóng</Button>} destroyOnClose>
      <Space size={6} wrap style={{ marginBottom: 8 }}>
        <Tag color={chip.tone === 'ok' ? 'green' : chip.tone === 'warn' ? 'orange' : undefined} title={chip.title}>{chip.text}</Tag>
        {notes.map((n) => <Text key={n} type="warning" style={{ fontSize: 12 }}>{n}</Text>)}
      </Space>
      {/* TopView: mô tả chữ mâu thuẫn ảnh thì model phân xử NGẪU NHIÊN (TD-FILM-103, đo 24/09). */}
      <Alert type="info" showIcon style={{ marginBottom: 8, fontSize: 12 }}
        message="Ảnh bộ đồ sẽ lấy khuôn mặt từ ảnh bạn chọn; mô tả (tóc, mặt) mâu thuẫn với ảnh thì sửa mô tả cho khớp." />
      <Tabs size="small" activeKey={tab} onChange={(k) => { setTab(k as TabKey); if (k === 'mine' && !myAssets.length) loadMyAssets(); }} items={[{
        key: 'library',
        label: 'Thư viện avatar',
        children: (<>
          <Space wrap style={{ marginBottom: 8 }}>
            <Select size="small" style={{ width: 150 }} value={gender} onChange={(v) => { setGender(v); loadCatalog({ gender: v }); }}
              options={[{ value: 'auto', label: 'Giới theo nhân vật' }, { value: 'female', label: 'Nữ' }, { value: 'male', label: 'Nam' }]} />
            <Select size="small" style={{ width: 110 }} value={age} onChange={(v) => { setAge(v); loadCatalog({ age: v }); }}
              options={AGE_BUCKETS.map((b) => ({ value: b.key, label: b.label }))} />
            <Select size="small" style={{ width: 160 }} allowClear showSearch placeholder="Quốc gia" value={country}
              onChange={(v) => { setCountry(v); loadCatalog({ country: v }); }} options={COUNTRIES.map((x) => ({ value: x, label: x }))} />
            <Input.Search size="small" placeholder="Tìm tên / nghề / quốc gia" allowClear value={search} style={{ width: 200 }}
              onChange={(e) => setSearch(e.target.value)} onSearch={(v) => loadCatalog({ search: v })} />
            <Button size="small" icon={<ReloadOutlined />} loading={loading} onClick={() => loadCatalog()} />
          </Space>
          <Paragraph type="secondary" style={{ fontSize: 11, marginBottom: 6 }}>
            Xếp theo độ hợp với mô tả nhân vật (giới · tuổi · sắc tộc). Avatar mờ là đang dùng cho nhân vật khác trong truyện.
          </Paragraph>
          {/* Dev 24/09: 35/283 nhân vật có cột giới; casting ít khi nêu giới ⇒ "theo nhân vật" thường KHÔNG lọc được (ca Livia). */}
          {gender === 'auto' && c.gender !== 'female' && c.gender !== 'male' && (
            <Paragraph type="warning" style={{ fontSize: 12, marginBottom: 6 }}>
              {c.name} chưa rõ giới — danh sách gồm cả nam lẫn nữ. Chọn Nữ / Nam ở ô lọc (hoặc sửa giới trên thẻ).
            </Paragraph>
          )}
          <div style={{ maxHeight: 340, overflowY: 'auto' }}>
            {catalog.length === 0
              ? <Empty description={loading ? 'Đang tải…' : 'Không có avatar khớp bộ lọc'} />
              : <Row gutter={[8, 8]}>{catalog.map(avatarCard)}</Row>}
          </div>
          {dcForm}
        </>),
      }, {
        key: 'mine',
        label: `My Asset${myAssets.length ? ` (${myAssets.length})` : ''}`,
        children: (<>
          <Space style={{ marginBottom: 8 }}>
            <Button size="small" icon={<ReloadOutlined />} loading={myLoading} onClick={() => loadMyAssets(true)}>Làm mới</Button>
            <Text type="secondary" style={{ fontSize: 11 }}>
              Ảnh bạn tự upload lên ModelArk. Link ảnh hết hạn ~11.5 giờ — thứ được lưu là asset id nên gán xong vẫn dùng lâu dài.
            </Text>
          </Space>
          <div style={{ maxHeight: 340, overflowY: 'auto' }}>
            {myLoading ? <Empty description="Đang tải…" />
              : myAssets.length === 0 ? <Empty description='Chưa có asset nào — upload trên console ModelArk rồi bấm "Làm mới"' />
              : <Row gutter={[8, 8]}>
                  {myAssets.map((a) => (
                    <Col key={a.assetId} xs={12} sm={8} md={6}>
                      <Card size="small" hoverable styles={{ body: { padding: 6 } }} style={{ border: pickedId === a.assetId ? '2px solid #1677ff' : undefined }}
                        // KHÔNG đặt `picked`: ảnh My Asset là link sắp hết hạn ⇒ để server tải về R2 qua previewSourceUrl.
                        onClick={() => { setPicked(null); form.setFieldsValue({ seedanceCharacterId: a.assetId, previewSourceUrl: a.previewUrl || '' }); }}
                        cover={a.previewUrl
                          ? <img src={a.previewUrl} alt={a.title || a.assetId} style={{ height: 130, objectFit: 'cover' }} />
                          : <div style={{ height: 130, background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><UserOutlined /></div>}>
                        <div style={{ fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.title || a.assetId}</div>
                      </Card>
                    </Col>
                  ))}
                </Row>}
          </div>
          {dcForm}
        </>),
      }, {
        key: 'upload',
        label: 'Tải ảnh mặt lên',
        children: (<>
          <Alert type="warning" showIcon style={{ marginBottom: 8, fontSize: 12 }} message={<>
            Ảnh tải lên chỉ dùng cho <b>ảnh bộ đồ</b>; clip qua BytePlus vẫn cần avatar / look-asset (tới Epic 5).<br />
            Chỉ dùng <b>ảnh AI hoặc ảnh đã được phép dùng</b> — ảnh bộ đồ dựng từ ảnh này sẽ tự đăng ký KYC trên AVIS, <b>không xoá được</b>.
          </>} />
          {showUploadedFaceBlock(c) && (
            <Space align="center" style={{ marginBottom: 8 }}>
              <Image src={c.faceRefUrl || undefined} height={90} style={{ objectFit: 'cover', borderRadius: 4 }} />
              <div>
                <div style={{ fontSize: 12 }}>Ảnh mặt đã tải {c.faceSource === 'UPLOAD' && <Tag color="blue">Đang dùng</Tag>}</div>
                {c.faceSource !== 'UPLOAD' && (
                  <Button size="small" loading={busy === 'UPLOAD'} onClick={() => chooseSource('UPLOAD', `Ảnh bộ đồ của ${c.name} dựng từ ảnh mặt đã tải`)}>Dùng lại ảnh này</Button>
                )}
              </div>
            </Space>
          )}
          <Upload.Dragger accept="image/jpeg,image/png,image/webp" multiple={false} showUploadList={false}
            beforeUpload={(f) => { setFile(f); setFilePreview(URL.createObjectURL(f)); return false; }}>
            {filePreview
              ? <img src={filePreview} alt="" style={{ maxHeight: 180, maxWidth: '100%', objectFit: 'contain' }} />
              : <><p className="ant-upload-drag-icon"><InboxOutlined /></p><p>Kéo thả hoặc bấm để chọn ảnh mặt (JPG / PNG / WEBP, cạnh ngắn ≥ 300 px, một người)</p></>}
          </Upload.Dragger>
          <div style={{ textAlign: 'right', marginTop: 8 }}>
            <Button type="primary" disabled={!file} loading={busy === 'upload'} onClick={uploadFace}>Tải lên và dùng ảnh này</Button>
          </div>
        </>),
      }, {
        key: 'text',
        label: 'Tạo mặt (AI)',
        children: (<>
          <Paragraph style={{ fontSize: 13 }}>
            Mỗi lần bấm tạo <b>một chân dung</b> (áo trơn, hợp thể loại và bối cảnh của phim); muốn mặt khác thì <b>sửa ô Prompt</b> rồi tạo lại. <b>Dùng mặt này</b> sẽ dựng lại ảnh bộ chính bằng mặt đó
            (~1 phút); các bộ khác hiện cờ "dựng từ mặt cũ". Mỗi lần tạo tốn tiền thật — đừng bấm lặp.
          </Paragraph>
          {c.seedanceCharacterId && <Paragraph type="secondary" style={{ fontSize: 12 }}>Avatar đang gán vẫn giữ cho clip BytePlus (tới Epic 5).</Paragraph>}
          <Space style={{ marginBottom: 8 }}>
            <Button type="primary" icon={<ReloadOutlined />} loading={candLoading} disabled={!!choosingId} onClick={createCandidates}>
              Tạo mặt
            </Button>
          </Space>
          <div style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text strong style={{ fontSize: 12 }}>Prompt</Text>
              <Button type="link" size="small" style={{ padding: 0 }} disabled={candLoading} onClick={() => { setPromptText(''); setPromptEdited(true); setPromptRev((n) => n + 1); }}>Về prompt mặc định</Button>
            </div>
            <PromptChipEditor key={promptRev} inline value={promptText} extraChoices={chipChoices} minHeight={96}
              onChange={(t) => { setPromptText(t); setPromptEdited(true); }} />
            <Text type="secondary" style={{ fontSize: 11 }}>
              {cands.length ? 'Điền sẵn prompt của mặt mới nhất — sửa rồi bấm Tạo mặt. Có prompt tự viết thì gửi nguyên văn (chip thành tên trong câu).' : 'Để trống = dùng prompt mặc định (dựng từ hồ sơ + thể loại + bối cảnh của phim).'}
            </Text>
          </div>
          {candErr && <Alert type="error" showIcon style={{ marginBottom: 8 }} message={candErr}
            action={<Button size="small" onClick={createCandidates} disabled={candLoading}>Thử lại</Button>} />}
          {candWarn.map((w, i) => <Alert key={i} type="warning" showIcon style={{ marginBottom: 8 }} message={w} />)}
          {candLoading
            ? <Row gutter={[8, 8]}><Col xs={12} sm={6}><Card size="small" loading style={{ height: 190 }} /></Col></Row>
            : cands.length === 0
              ? <Empty description="Chưa có mặt nào — bấm Tạo mặt" />
              : (
                <Row gutter={[8, 8]}>
                  {cands.map((v) => {
                    const inUse = isCandidateInUse(c, v);
                    return (
                      <Col key={v.id} xs={12} sm={6}>
                        <Card size="small" styles={{ body: { padding: 6 } }} style={{ border: inUse ? '2px solid #1677ff' : undefined }}
                          cover={<img src={v.url} alt="" style={{ height: 170, objectFit: 'cover', objectPosition: 'top' }} />}>
                          <div style={{ fontSize: 11, marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={v.model || ''}>
                            {faceCandidateModelLabel(v.model)} {inUse && <Tag color="blue" style={{ fontSize: 10 }}>Đang dùng</Tag>}
                          </div>
                          <Popconfirm title="Dùng mặt này?" okText="Dùng và dựng lại" cancelText="Thôi" onConfirm={() => chooseCandidate(v)}
                            description="Sẽ dựng lại ảnh bộ chính bằng mặt này (~1 phút); các bộ khác hiện cờ 'dựng từ mặt cũ'.">
                            <Button size="small" block type={inUse ? 'default' : 'primary'} loading={choosingId === v.id} disabled={!!choosingId && choosingId !== v.id}>
                              {inUse ? 'Dựng lại bộ chính' : 'Dùng mặt này'}
                            </Button>
                          </Popconfirm>
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
              )}
          <div style={{ marginTop: 12 }}>
            <Button type="link" size="small" style={{ padding: 0 }} disabled={c.faceSource === 'TEXT' || !!choosingId} loading={busy === 'TEXT'}
              onClick={() => chooseSource('TEXT', `Ảnh bộ đồ của ${c.name} gen từ mô tả`)}>
              {c.faceSource === 'TEXT' ? 'Đang dùng mô tả (không ảnh mặt)' : 'Dùng mô tả, không ảnh mặt'}
            </Button>
          </div>
        </>),
      }]} />
    </Modal>
  );
};

export default FaceSourceModal;
